// @flow
import React, {useState} from 'react';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {makeStyles} from '@material-ui/styles';

import {
  Grid,
  Theme,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import Section from './Section';
import {colors} from '../../../../../assets/styles/colors';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useIsFeatureInTier} from '../../../../../utils/component';

import type {ReportRequest} from '../../api';
import {useIsMinimalPlan} from '../../../../store/utils';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {
  useStoreAnalyticsActions,
  useStoreGoogleIntegrationInfo,
} from '../../utils';
import {useAlert} from '../../../../../elements/Alert/AlertContext';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';
import {Delete} from '@material-ui/icons';

type Props = {
  updateGAIntegration: ({reportRequest: ReportRequest}) => void,
  trackingId?: string,
  isSaving?: boolean,
  saveSuccess?: boolean,
};

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontWeight: 600,
    fontSize: 18,
    color: colors.grey900,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  status: {
    fontSize: 14,
    color: colors.grey700,
  },
  description: {
    color: '#9595AB',
    fontSize: 14,

    '& a': {
      color: colors.primary,
    },
  },
  buttonContainer: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '1rem',
  },
  tabsRoot: {
    borderBottom: `1px solid ${colors.grey300}`,
    marginBottom: '.5rem',
  },
  tab: {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  snippetTextField: {
    '& textarea': {
      fontFamily: 'monospace',
      fontSize: 12,
    },
  },
  extractedIdBox: {
    padding: '.5rem',
    backgroundColor: colors.grey100,
    borderRadius: '.75rem',
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  extractedIdText: {
    fontSize: 14,
    color: colors.grey900,
    fontWeight: 500,
  },
}));

const extractTrackingId = (snippet: string): string | null => {
  if (!snippet) return null;

  // GA4 pattern: gtag('config', 'G-XXXXXXXXXX')
  const ga4Match = snippet.match(
    /gtag\s*\(\s*['"]config['"]\s*,\s*['"]([G]-[A-Z0-9]+)['"]/i
  );
  if (ga4Match) return ga4Match[1];

  // Universal Analytics pattern: ga('create', 'UA-XXXXXXXXX-X')
  const uaMatch = snippet.match(
    /ga\s*\(\s*['"]create['"]\s*,\s*['"]([UA]-[0-9]+-[0-9]+)['"]/i
  );
  if (uaMatch) return uaMatch[1];

  // Direct ID pattern in text
  const directMatch = snippet.match(/([UG][A]?-[A-Z0-9]+-?[0-9]*)/i);
  if (directMatch) return directMatch[1];

  return null;
};

export default function GoogleAnalyticsSection(props: Props) {
  const {updateGAIntegration, isSaving} = props;
  const classes = useStyles();

  const isMinimalPlan = useIsMinimalPlan();
  const {register, watch, errors, setValue} = useForm();
  const hasGAIntegrationFeature = useIsFeatureInTier(
    'GOOGLE_ANALYTICS_TRACKING'
  );

  const hasGTMIntegrationFeature = useIsFeatureInTier('GOOGLE_TAG_MANAGER');

  const {gtmProvider, googleAnalyticsId} = useStoreGoogleIntegrationInfo();

  const [googleTagManagerId, setGoogleTagManagerId] = useState(
    gtmProvider?.trackingId || ''
  );

  const [codeSnippet, setCodeSnippet] = useState('');
  const [extractedId, setExtractedId] = useState('');

  const {
    createStoreGTM,
    updateStoreGTM,
    deleteStoreGTM,
  } = useStoreAnalyticsActions();

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const {showAlert} = useAlert();

  const onSubmit = (trackingId: string) => {
    const reportRequest = {
      trackingId: trackingId.trim(),
    };
    updateGAIntegration({reportRequest});

    setExtractedId('');
    setCodeSnippet('');
  };

  const handleSnippetChange = event => {
    const snippet = event.target.value;
    setCodeSnippet(snippet);

    // Extract tracking ID from snippet
    const id = extractTrackingId(snippet);
    if (id) {
      setExtractedId(id);
      if (!googleAnalyticsId) {
        setValue('trackingId', id);
      }
    } else {
      setExtractedId('');
    }
  };

  useEffect(() => {
    setGoogleTagManagerId(gtmProvider?.trackingId || '');
  }, [gtmProvider]);

  useEffect(() => {
    setValue('trackingId', googleAnalyticsId);
  }, [setValue, googleAnalyticsId]);

  const handleClickGtmButton = () => {
    if (!hasGTMIntegrationFeature) return showTierRestrictionModal();
    const duration = 5000;
    try {
      if (!gtmProvider) {
        return createStoreGTM(googleTagManagerId).then(() =>
          showAlert('Google Tag Manager configurado correctamente', {
            type: 'success',
            duration,
          })
        );
      }

      if (gtmProvider.trackingId === googleTagManagerId) {
        showAlert('No se realizaron cambios', {
          type: 'info',
          duration,
        });
        return;
      }
      if (gtmProvider.trackingId && googleTagManagerId.length === 0) {
        return deleteStoreGTM().then(() =>
          showAlert('Google Tag Manager eliminado correctamente', {
            type: 'success',
            duration,
          })
        );
      }

      updateStoreGTM(googleTagManagerId).then(() =>
        showAlert('Google Tag Manager actualizado correctamente', {
          type: 'success',
          duration,
        })
      );
    } catch (error) {
      showAlert('Ocurrió un error al guardar los cambios', {
        type: 'error',
        duration,
      });
    }
  };

  const handleReplaceGoogleAnalyticsId = () => {
    setValue('trackingId', extractedId);

    onSubmit(extractedId);

    setExtractedId('');
    setCodeSnippet('');
  };

  const handleRemoveGoogleAnalyticsId = () => {
    setValue('trackingId', '');
    setExtractedId('');
    setCodeSnippet('');
  };

  const watchGoogleAnalyticsId = watch('trackingId');

  return (
    <div className={classes.root}>
      <Section
        leftContent={
          <Typography className={classes.title}>
            Google Analytics {isMinimalPlan && <PremiumFeatureBadge />}
          </Typography>
        }
      >
        <div className={classes.content}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Obtén más datos que te ayudarán a medir y mejorar la conversión
                de tu tienda. Copia y pega tu ID de Google Analytics o el código
                completo y monitorea los datos de tu tienda en el tablero de
                Google Analytics.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                placeholder="G-XXXXXXXXXX o UA-XXXXXXXXX-X"
                className={classes.categoryTextField}
                value={watchGoogleAnalyticsId}
                name="trackingId"
                error={!!errors.trackingId}
                helperText={errors.trackingId ? errors.trackingId.message : ''}
                inputRef={register({
                  required: 'Campo obligatorio',
                })}
                type="text"
                disabled={isSaving || !hasGAIntegrationFeature}
                InputProps={{
                  endAdornment: watchGoogleAnalyticsId && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleRemoveGoogleAnalyticsId}
                      >
                        <Delete color="error" size="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Typography
              variant="body2"
              style={{textAlign: 'center', marginInline: 'auto'}}
              className={classes.description}
            >
              o copia y pega el código completo de Google Analytics
            </Typography>

            <Grid item xs={12}>
              <Box>
                <CustomTextField
                  placeholder="Pega aquí el código completo de Google Analytics..."
                  className={classes.snippetTextField}
                  value={codeSnippet}
                  onChange={handleSnippetChange}
                  multiline
                  rows={8}
                  disabled={isSaving || !hasGAIntegrationFeature}
                />
                {extractedId && (
                  <Box mt={2} className={classes.extractedIdBox}>
                    <Typography className={classes.extractedIdText}>
                      ID detectado: {extractedId}
                    </Typography>
                    {googleAnalyticsId && (
                      <CustomButton
                        variant="outlined"
                        onClick={handleReplaceGoogleAnalyticsId}
                      >
                        Reemplazar ID
                      </CustomButton>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.buttonContainer}>
                <CustomButton
                  disabled={isSaving || !hasGAIntegrationFeature}
                  variant="contained"
                  onClick={() => onSubmit(watchGoogleAnalyticsId)}
                >
                  Guardar cambios
                </CustomButton>
              </div>
            </Grid>
          </Grid>
        </div>
      </Section>
      <Section
        leftContent={
          <Typography className={classes.title}>
            Google Tag Manager{' '}
            {!hasGTMIntegrationFeature && <PremiumFeatureBadge />}
          </Typography>
        }
      >
        <div className={classes.content}>
          <Grid item>
            <Typography className={classes.description}>
              Obtén información detallada para optimizar tu tienda. Copia y pega
              tu ID de Google Tag Manager y supervisa las actividades de tu
              tienda directamente desde el panel de{' '}
              <a
                href="https://tagmanager.google.com/#/home"
                target="_BLANK"
                rel="noopener noreferrer"
              >
                Google Tag Manager.
              </a>
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomTextField
                placeholder="GTM-XXXXXX"
                className={classes.categoryTextField}
                name="gtmId"
                error={!!errors.trackingId}
                value={googleTagManagerId}
                helperText={errors.trackingId ? errors.trackingId.message : ''}
                type="text"
                disabled={isSaving || !hasGTMIntegrationFeature}
                onChange={e => {
                  setGoogleTagManagerId(e.target.value.trim());
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <div className={classes.buttonContainer}>
                <CustomButton
                  disabled={isSaving || !hasGTMIntegrationFeature}
                  variant="contained"
                  onClick={handleClickGtmButton}
                >
                  Guardar cambios
                </CustomButton>
              </div>
            </Grid>
          </Grid>
        </div>
      </Section>
    </div>
  );
}
